import { useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { CODE } from "../../utils/Constant";

interface TimeEditorManagerProps {
    name: string;
    idFieldName?: string;
    values: any;
    updateFields: (orderGroupId: string, fields: Record<string, any>) => Promise<any>;
    onUpdateSuccess?: (updatedData: any) => void;
    loadTimeEditorInfo?: (orderGroupId: string) => Promise<any>; // ⚡ Lazy load timeEditor info
}

/**
 * Custom Hook quản lý logic edit/update timeEditor time
 * Dùng chung cho cả khám bệnh và điều trị
 */
export const useTimeEditorManager = ({
    name,
    idFieldName = "id",
    values,
    updateFields,
    onUpdateSuccess,
    loadTimeEditorInfo
}: TimeEditorManagerProps) => {
    const [isEditingTime, setIsEditingTime] = useState(false);
    const [editedTime, setEditedTime] = useState("");
    const [showValidationConfirm, setShowValidationConfirm] = useState(false);
    const [showConfirmWithReason, setShowConfirmWithReason] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [isLoadingTimeEditorInfo, setIsLoadingTimeEditorInfo] = useState(false);

    const handleEditTime = async () => {
        setIsEditingTime(true);

        // ⚡ Lazy load: Nếu chưa có timeEditorTime và có loadTimeEditorInfo, load ngay
        if (!values?.[name] && loadTimeEditorInfo && values?.[idFieldName]) {
            setIsLoadingTimeEditorInfo(true);
            try {
                const timeEditorInfo = await loadTimeEditorInfo(values[idFieldName]);
                if (timeEditorInfo?.[name]) {
                    setEditedTime(timeEditorInfo[name]);
                } else {
                    setEditedTime(moment().format("YYYY-MM-DDTHH:mm:ss"));
                }
            } catch (error) {
                console.error("Error loading timeEditor info:", error);
                setEditedTime(moment().format("YYYY-MM-DDTHH:mm:ss"));
            } finally {
                setIsLoadingTimeEditorInfo(false);
            }
        } else {
            setEditedTime(
                values?.[name] || moment().format("YYYY-MM-DDTHH:mm:ss")
            );
        }
    };

    const handleCancelEdit = () => {
        setIsEditingTime(false);
        setEditedTime("");
    };

    const handleSaveTime = async (submitData?: any, allowDisableRules?: boolean) => {
        try {
            if (!editedTime) {
                toast.error("Vui lòng chọn thời gian hợp lệ");
                return;
            }

            const payload: any = {
                ...(submitData ?? {}),
                [name]: editedTime
            };

            if (allowDisableRules) {
                payload.allowDisableRules = true;
            }

            const { data } = await updateFields(values?.[idFieldName], payload);

            if (data?.code === CODE.SUCCESS) {
                toast.success("Cập nhật thành công");
                setIsEditingTime(false);
                setShowValidationConfirm(false);
                setShowConfirmWithReason(false);

                // Callback để parent component cập nhật state
                onUpdateSuccess?.({
                    ...(data?.data ?? {}),
                    [name]: editedTime
                });
            } else if (data?.code === CODE.ERROR_CODE_400 && data?.data?.[0]?.errorMessage) {
                // Có lỗi validation, hiển thị popup confirm
                setValidationMessage(data.data[0].errorMessage);
                setShowConfirmWithReason(false);
                setShowValidationConfirm(true);
            } else {
                toast.error(data?.message || "Cập nhật thất bại");
                setShowConfirmWithReason(false);
            }
        } catch (error: any) {
            console.error(error);
            setShowConfirmWithReason(false);
            const errorData = error?.response?.data;
            if (errorData?.code === CODE.ERROR_CODE_400 && errorData?.data?.[0]?.errorMessage) {
                // Có lỗi validation từ backend
                setValidationMessage(errorData.data[0].errorMessage);
                setShowValidationConfirm(true);
            } else {
                toast.error(errorData?.message || "Có lỗi xảy ra khi cập nhật");
            }
        }
    };

    const handleConfirmValidation = () => {
        setShowValidationConfirm(false);
        handleSaveTime(true);
    };

    const handleCancelValidation = () => {
        setShowValidationConfirm(false);
    };

    const handleConfirmWithReason = () => {
        setShowConfirmWithReason(true);
    };

    const handleCancelWithReason = () => {
        setShowConfirmWithReason(false);
    };

    return {
        // States
        isEditingTime,
        editedTime,
        showValidationConfirm,
        showConfirmWithReason,
        validationMessage,
        isLoadingTimeEditorInfo,

        // Setters
        setEditedTime,

        // Handlers
        handleEditTime,
        handleCancelEdit,
        handleSaveTime,
        handleConfirmValidation,
        handleCancelValidation,
        handleConfirmWithReason,
        handleCancelWithReason,
    };
};
